import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { STAFF_ROUTE } from "../../../../app/router/routes";
import { ILocalDate } from "../../../../entities/calendar";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { IShift, IShiftStaffAssignment, shiftService, TShiftStatus } from "../../../../entities/shift";
import { ITeam, teamService } from "../../../../entities/team";
import { ShiftAssign } from "../../../../features/shiftAssign";
import { ShiftReplaceStaff, TShiftReplaceStaffMode } from "../../../../features/shiftReplaceStaff";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { MyError } from "../../../../shared/lib/error/MyError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import classes from './shiftCalendarDay.module.scss'
import {getMoneyLabel} from './shiftCost'

interface IProps {
    date: Date;
    tgAccountIds?: string[];
    onBack: () => void;
}

interface IReplaceState {
    mode: TShiftReplaceStaffMode;
    teamId: string;
    teamName: string;
    workTgIds: number[];
    tgAccountId?: string;
    tgAccountName?: string;
    currentUserId?: string;
    curatorReplacementOptions?: {
        id: number;
        name: string;
    }[];
    coordinatorReplacementOption?: {
        id: number;
        name: string;
    };
}

const getLocalDateFromDate = (date: Date): ILocalDate => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
})

const formatDate = (date: Date): string => {
    return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
    })
}

const formatTime = (date?: string): string => {
    if(!date) return '—'

    const parsedDate = new Date(date)
    if(Number.isNaN(parsedDate.getTime())) return '—'

    return parsedDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow',
    })
}

const getStatusLabel = (status?: TShiftStatus): string => {
    switch(status){
        case 'SHIFT_STATUS_PLANNED':
            return 'Запланирована'
        case 'SHIFT_STATUS_COMPLETED':
            return 'Завершена'
        case 'SHIFT_STATUS_CANCELLED':
            return 'Отменена'
        default:
            return 'Нет смены'
    }
}

const getTeamStaffCost = (staff?: ITeam['coordinators'][number]): {label: string; isMissing: boolean} => {
    return getMoneyLabel(staff?.costPerShift || staff?.cost_per_shift)
}

const getShiftStaffCost = (assignment?: IShiftStaffAssignment): {label: string; isMissing: boolean} => {
    const actual = assignment?.actualStaff
    const planned = assignment?.plannedStaff

    return getMoneyLabel(
        actual?.costPerShift
        || actual?.cost_per_shift
        || planned?.costPerShift
        || planned?.cost_per_shift
    )
}

const getStaffPath = (userId?: string): string => `${STAFF_ROUTE.path}/${userId || ''}`

const renderStaffName = (userId: string | undefined, fullName: string) => {
    if(!userId){
        return fullName
    }

    return (
        <Link className={classes.staffLink} to={getStaffPath(userId)}>
            {fullName}
        </Link>
    )
}

const StaffAssignment: FC<{
    title: string;
    assignment?: IShiftStaffAssignment;
    defaultStaffId?: number;
    defaultStaffName?: string;
    costPerShift?: {label: string; isMissing: boolean};
    hideCost?: boolean;
    action?: ReactNode;
}> = ({title, assignment, defaultStaffId = 0, defaultStaffName = '', costPerShift, hideCost = false, action}) => {
    const plannedName = assignment?.plannedStaff?.fullName || 'Не назначен'
    const actualName = assignment?.actualStaff?.fullName || plannedName
    const plannedUserId = assignment?.plannedStaff?.userId
    const actualUserIdString = assignment?.actualStaff?.userId || assignment?.plannedStaff?.userId
    const actualUserId = Number(assignment?.actualStaff?.userId ?? assignment?.plannedStaff?.userId ?? 0)
    const hasReplacement = Boolean(
        assignment?.actualStaff?.userId &&
        assignment?.plannedStaff?.userId &&
        assignment.actualStaff.userId !== assignment.plannedStaff.userId
    )
    const hasShiftStaffChange = Boolean(!hasReplacement && defaultStaffId && actualUserId && actualUserId !== defaultStaffId)
    const shiftCost = getShiftStaffCost(assignment)
    const resolvedCostPerShift = hideCost ? undefined : (shiftCost.isMissing ? costPerShift || shiftCost : shiftCost)

    return (
        <article className={`${classes.staffCard} ${hasReplacement ? classes.replaced : ''} ${hasShiftStaffChange ? classes.changed : ''}`}>
                <section className={classes.staffHeader}>
                    <h4>{title}</h4>
                    <section className={classes.staffHeaderActions}>
                        {action}
                        {hasShiftStaffChange && <span className={classes.replacementBadge}>Изменено</span>}
                        {hasReplacement && <span className={classes.replacementBadge}>Замена</span>}
                    </section>
                </section>

            <section className={classes.staffRows}>
                <p>
                    <span>План</span>
                    <b>{renderStaffName(plannedUserId, plannedName)}</b>
                </p>
                <p>
                    <span>Факт</span>
                    <b className={classes.actualStaffInfo}>
                        <span className={classes.actualStaffName}>{renderStaffName(actualUserIdString, actualName)}</span>
                        {resolvedCostPerShift && (
                            <small className={`${classes.staffCost} ${resolvedCostPerShift.isMissing ? classes.staffCostMissing : ''}`}>
                                {resolvedCostPerShift.label}
                            </small>
                        )}
                    </b>
                </p>
                {hasShiftStaffChange && (
                    <p className={classes.replacementText}>
                        <span>Замена</span>
                        <b>(вместо {defaultStaffName})</b>
                    </p>
                )}
                {assignment?.replacementComment && (
                    <p className={classes.comment}>
                        <span>Комментарий</span>
                        <b>{assignment.replacementComment}</b>
                    </p>
                )}
            </section>
        </article>
    )
}

export const ShiftCalendarDay: FC<IProps> = ({date, tgAccountIds = [], onBack}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [shift, setShift] = useState<IShift | undefined>()
    const [availableActions, setAvailableActions] = useState<IShift['availableActions']>([])
    const [teams, setTeams] = useState<ITeam[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isAssignOpen, setIsAssignOpen] = useState<boolean>(false)
    const [replaceState, setReplaceState] = useState<IReplaceState | null>(null)
    const [reloadIndex, setReloadIndex] = useState<number>(0)

    const localDate = useMemo(() => getLocalDateFromDate(date), [date])
    const tgAccountIdsKey = tgAccountIds.join(',')
    const selectedWorkTgIds = useMemo(
        () => tgAccountIdsKey.split(',').map(id => Number(id)).filter(id => !Number.isNaN(id) && id > 0),
        [tgAccountIdsKey]
    )

    useEffect(() => {
        let isActual = true
        setIsLoading(true)
        setShift(undefined)
        setAvailableActions([])
        setTeams([])

        const getData = async() => {
            try{
                const [response, teamsResponse] = await Promise.all([
                    shiftService.getShift({
                        date: localDate,
                        tgAccountIds: selectedWorkTgIds.map(String),
                    }),
                    teamService.list(selectedWorkTgIds),
                ])

                if(isActual){
                    setShift(response.shift)
                    setAvailableActions(response.availableActions)
                    setTeams(teamsResponse)
                }
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении смены на день', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [localDate, tgAccountIdsKey, reloadIndex])

    const onAssignSuccess = (nextShift: IShift) => {
        setShift(nextShift)
        setReloadIndex(current => current + 1)
    }

    const onReplaceSuccess = (nextShift: IShift) => {
        setShift(nextShift)
        setReplaceState(null)
        setReloadIndex(current => current + 1)
    }

    if(isLoading){
        return (
            <section className={classes.container}>
                <button type="button" className={classes.backButton} onClick={onBack}>
                    <span aria-hidden="true">‹</span>
                    Назад
                </button>
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
            </section>
        )
    }

    const canAssignEmptyShift = availableActions.includes('SHIFT_ACTION_ASSIGN')

    if(!shift){
        return (
            <section className={classes.container}>
                <button type="button" className={classes.backButton} onClick={onBack}>
                    <span aria-hidden="true">‹</span>
                    Назад
                </button>
                <section className={classes.header}>
                    <section>
                        <h2>{formatDate(date)}</h2>
                        <p>Смена на выбранную дату не назначена</p>
                    </section>
                    <span className={classes.status}>{getStatusLabel()}</span>
                </section>
                <p className={classes.empty}>На этот день смен нет</p>
                {canAssignEmptyShift ? (
                    <>
                        <section className={classes.actions}>
                            <button type="button" className={classes.primaryAction} onClick={() => setIsAssignOpen(true)}>
                                Назначить смену
                            </button>
                        </section>
                        <ShiftAssign
                            open={isAssignOpen}
                            setOpen={setIsAssignOpen}
                            date={localDate}
                            workTgIds={selectedWorkTgIds}
                            onSuccess={onAssignSuccess}
                        />
                    </>
                ) : (
                    <section className={classes.notice}>
                        Нет доступного действия для назначения смены на эту дату.
                    </section>
                )}
            </section>
        )
    }

    const canAssignShift = shift.availableActions.includes('SHIFT_ACTION_ASSIGN')
    const canReplaceCoordinator = shift.availableActions.includes('SHIFT_ACTION_REPLACE_COORDINATOR')
    const canReplaceCurator = shift.availableActions.includes('SHIFT_ACTION_REPLACE_CURATOR')

    return (
        <section className={classes.container}>
            <button type="button" className={classes.backButton} onClick={onBack}>
                <span aria-hidden="true">‹</span>
                Назад
            </button>
            <section className={classes.header}>
                <section>
                    <h2>{formatDate(date)}</h2>
                    <p>{formatTime(shift.startsAt)} — {formatTime(shift.endsAt)}</p>
                </section>
                <span className={`${classes.status} ${classes[shift.status]}`}>{getStatusLabel(shift.status)}</span>
            </section>

            {canAssignShift && (
                <section className={classes.actions}>
                    <button type="button" className={classes.primaryAction} onClick={() => setIsAssignOpen(true)}>
                        Изменить состав смены
                    </button>
                </section>
            )}

            <section className={classes.section}>
                <h3>Главрук</h3>
                <StaffAssignment title="Ответственный за смену" assignment={shift.headTeacher} hideCost />
            </section>

            <section className={classes.section}>
                <h3>Звенья</h3>
                {!shift.teams.length && <p className={classes.empty}>Звенья не назначены</p>}
                <section className={classes.teams}>
                    {shift.teams.map(team => {
                        const teamDetails = teams.find(item => item.id === team.teamId)
                        const defaultCoordinator = teamDetails?.coordinators[0]
                        const defaultCoordinatorCost = getTeamStaffCost(defaultCoordinator)
                        const teamWorkTgIds = teamDetails?.tgAccounts.map(item => Number(item.id)) ?? team.curators.map(item => Number(item.tgAccount.id)).filter(Boolean)

                        return (
                        <article className={classes.teamCard} key={team.id || team.teamId}>
                            <section className={classes.teamHeader}>
                                <h4>{team.teamName || `Звено ${team.teamId}`}</h4>
                                <span>{team.curators.length} TG</span>
                            </section>

                            <StaffAssignment
                                title="Координатор"
                                assignment={team.coordinator}
                                defaultStaffId={defaultCoordinator ? Number(defaultCoordinator.userId) : undefined}
                                defaultStaffName={defaultCoordinator?.fullName}
                                costPerShift={defaultCoordinatorCost}
                                action={canReplaceCoordinator ? (
                                    <button
                                        type="button"
                                        className={classes.replaceButton}
                                        onClick={() => setReplaceState({
                                            mode: 'coordinator',
                                            teamId: team.teamId,
                                            teamName: team.teamName,
                                            workTgIds: teamWorkTgIds,
                                            currentUserId: team.coordinator.actualStaff?.userId ?? team.coordinator.plannedStaff?.userId,
                                        })}
                                    >
                                        Заменить
                                    </button>
                                ) : null}
                            />

                            <section className={classes.curators}>
                                {team.curators.map(curator => {
                                    const defaultCurator = teamDetails
                                        ?.tgCurators
                                        .find(item => item.tgAccount.id === curator.tgAccount.id)
                                        ?.curators[0]
                                    const defaultCuratorCost = getTeamStaffCost(defaultCurator)
                                    const curatorReplacementOptions = teamDetails
                                        ?.tgCurators
                                        .find(item => item.tgAccount.id === curator.tgAccount.id)
                                        ?.curators
                                        .map(staff => ({
                                            id: Number(staff.userId),
                                            name: `${staff.userId}. ${staff.fullName}`.trim(),
                                        }))

                                    return (
                                    <StaffAssignment
                                        key={curator.tgAccount.id}
                                        title={`Куратор ${curator.tgAccount.title || curator.tgAccount.username || curator.tgAccount.id}`}
                                        assignment={curator.curator}
                                        defaultStaffId={defaultCurator ? Number(defaultCurator.userId) : undefined}
                                        defaultStaffName={defaultCurator?.fullName}
                                        costPerShift={defaultCuratorCost}
                                        action={canReplaceCurator ? (
                                            <button
                                                type="button"
                                                className={classes.replaceButton}
                                                onClick={() => setReplaceState({
                                                    mode: 'curator',
                                                    teamId: team.teamId,
                                                    teamName: team.teamName,
                                                    workTgIds: teamWorkTgIds,
                                                    tgAccountId: curator.tgAccount.id,
                                                    tgAccountName: curator.tgAccount.title || curator.tgAccount.username || curator.tgAccount.id,
                                                    currentUserId: curator.curator.actualStaff?.userId ?? curator.curator.plannedStaff?.userId,
                                                    curatorReplacementOptions,
                                                    coordinatorReplacementOption: team.coordinator.actualStaff?.userId
                                                        ? {
                                                            id: Number(team.coordinator.actualStaff.userId),
                                                            name: `${team.coordinator.actualStaff.userId}. ${team.coordinator.actualStaff.fullName}`.trim(),
                                                        }
                                                        : undefined,
                                                })}
                                            >
                                                Заменить
                                            </button>
                                        ) : null}
                                    />
                                    )
                                })}
                            </section>
                        </article>
                        )
                    })}
                </section>
            </section>

            <ShiftAssign
                open={isAssignOpen}
                setOpen={setIsAssignOpen}
                date={localDate}
                shift={shift}
                workTgIds={selectedWorkTgIds}
                onSuccess={onAssignSuccess}
            />
            {replaceState && (
                <ShiftReplaceStaff
                    open={Boolean(replaceState)}
                    setOpen={(open) => {
                        if(!open) setReplaceState(null)
                    }}
                    mode={replaceState.mode}
                    shift={shift}
                    teamId={replaceState.teamId}
                    teamName={replaceState.teamName}
                    workTgIds={replaceState.workTgIds}
                    tgAccountId={replaceState.tgAccountId}
                    tgAccountName={replaceState.tgAccountName}
                    currentUserId={replaceState.currentUserId}
                    curatorReplacementOptions={replaceState.curatorReplacementOptions}
                    coordinatorReplacementOption={replaceState.coordinatorReplacementOption}
                    onSuccess={onReplaceSuccess}
                />
            )}
        </section>
    )
}
